import { motion } from "framer-motion";
import { useEffect, useState } from "react";

export const HeroLoading = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) return 95;
        return prev + 1;
      });
    }, 30);

    return () => clearInterval(timer);
  }, []);

  // Calculate loading phase based on progress
  const getLoadingPhase = () => {
    if (progress < 33) return "Starting...";
    if (progress < 66) return "Loading resources...";
    return "Almost there...";
  };

  // Number of particles in the orbital ring
  const particleCount = 12;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 relative overflow-hidden">
      {/* Animated background blobs - mantidos por serem sutis e bonitos */}
      <motion.div
        className="absolute -top-1/2 -left-1/2 w-full h-full bg-primary/5 rounded-full blur-3xl"
        animate={{
          x: [0, 50, 0],
          y: [0, -30, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-accent/5 rounded-full blur-3xl"
        animate={{
          x: [0, -30, 0],
          y: [0, 50, 0],
          scale: [1, 0.9, 1],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
      />

      {/* Main content */}
      <div className="relative z-10 text-center px-6">
        {/* App name with animation */}
        <motion.h1
          className="text-3xl md:text-4xl font-bold mb-16 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          GlobalCoders - English for Tech Teams
        </motion.h1>

        {/* Orbital Particle Ring Loader */}
        <div className="mb-12 flex justify-center">
          <div className="relative w-48 h-48">
            {/* Central icon with breathing animation */}
            <motion.div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-gradient-to-br from-primary to-accent rounded-2xl shadow-2xl shadow-primary/30 flex items-center justify-center backdrop-blur-sm"
              animate={{
                scale: [1, 1.08, 1],
                rotate: [0, 5, -5, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <motion.svg
                className="w-10 h-10 text-primary-foreground"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                animate={{
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </motion.svg>

              {/* Inner glow */}
              <motion.div
                className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/40 to-accent/40 blur-xl"
                animate={{
                  opacity: [0.5, 0.8, 0.5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </motion.div>

            {/* Orbital Ring with Particles */}
            <motion.div
              className="absolute inset-0"
              animate={{
                rotate: 360,
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "linear",
              }}
            >
              {/* Ring gradient background */}
              <svg className="w-full h-full" viewBox="0 0 100 100">
                <defs>
                  <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.6" />
                    <stop offset="50%" stopColor="hsl(var(--accent))" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.6" />
                  </linearGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                
                {/* Base ring */}
                <circle
                  cx="50"
                  cy="50"
                  r="35"
                  fill="none"
                  stroke="url(#ringGradient)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray={`${progress * 2.2} ${220 - progress * 2.2}`}
                  filter="url(#glow)"
                  style={{
                    transition: "stroke-dasharray 0.3s ease-out",
                  }}
                />
              </svg>

              {/* Orbital Particles */}
              {Array.from({ length: particleCount }).map((_, i) => {
                const angle = (i / particleCount) * 360;
                const delay = (i / particleCount) * 0.8;
                const size = 4 + (i % 3);
                const opacity = 0.3 + ((i % 4) * 0.2);
                
                return (
                  <motion.div
                    key={i}
                    className="absolute top-1/2 left-1/2"
                    style={{
                      width: `${size}px`,
                      height: `${size}px`,
                    }}
                    initial={{
                      x: "-50%",
                      y: "-50%",
                    }}
                  >
                    <motion.div
                      className="w-full h-full rounded-full shadow-lg"
                      style={{
                        background: i % 2 === 0
                          ? "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--accent)))"
                          : "linear-gradient(135deg, hsl(var(--accent)), hsl(var(--primary)))",
                        boxShadow: `0 0 ${size * 2}px hsl(var(--primary) / ${opacity})`,
                      }}
                      animate={{
                        x: [
                          Math.cos((angle * Math.PI) / 180) * 90,
                          Math.cos(((angle + 360) * Math.PI) / 180) * 90,
                        ],
                        y: [
                          Math.sin((angle * Math.PI) / 180) * 90,
                          Math.sin(((angle + 360) * Math.PI) / 180) * 90,
                        ],
                        scale: [1, 1.3, 1],
                        opacity: [opacity, opacity + 0.3, opacity],
                      }}
                      transition={{
                        duration: 8,
                        repeat: Infinity,
                        ease: "linear",
                        delay: delay,
                      }}
                    />
                  </motion.div>
                );
              })}
            </motion.div>

            {/* Outer glow ring */}
            <motion.div
              className="absolute inset-0 rounded-full"
              style={{
                background: "radial-gradient(circle, transparent 60%, hsl(var(--primary) / 0.1) 70%, transparent 80%)",
              }}
              animate={{
                scale: [1, 1.05, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </div>
        </div>

        {/* Progress percentage - subtle and elegant */}
        <motion.div
          className="mb-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <motion.span
            className="text-4xl font-light bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent tabular-nums"
            key={progress}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {progress}%
          </motion.span>
        </motion.div>

        {/* Loading phase text */}
        <motion.p
          className="text-sm text-muted-foreground font-medium tracking-wide"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          key={getLoadingPhase()}
        >
          {getLoadingPhase()}
        </motion.p>
      </div>

      {/* Bottom decoration with shimmer effect */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary/40 to-transparent"
        animate={{
          opacity: [0.3, 0.8, 0.3],
          backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </div>
  );
};
